import { Injectable } from '@angular/core';
import { Firestore, doc, docData, collection, CollectionReference, DocumentReference, setDoc, updateDoc, arrayUnion, arrayRemove, deleteField, runTransaction } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { DataService } from './data';


// Interfaz para la configuración de un solo ministerio
export interface MinisterioConfig {
  id: string; // Identificador único del ministerio
  nombre: string; // Nombre visible
  roles: string[]; // Array de strings con los roles dentro de este ministerio
  activo: boolean; // Si el ministerio está activo
  orden: number; // Orden de visualización
  icono?: string; // Nombre opcional del ícono
}

// Interfaz para la estructura general del documento ministeriosRoles
export interface MinisteriosRolesConfig {
  ministerios: MinisterioConfig[];
  rolesGenerales?: string[]; // Opcional: roles generales configurables
}


@Injectable({
  providedIn: 'root'
})
export class ConfigService {

  constructor(
    private firestore: Firestore,
    private dataService: DataService
  ) { }

  /**
   * Obtiene la configuración de ministerios y roles para una iglesia específica.
   * @param churchId El ID de la iglesia.
   * @returns Un Observable de la configuración MinisteriosRolesConfig.
   */
  getMinisteriosRoles(churchId: string): Observable<MinisteriosRolesConfig | undefined> {
    const configDocRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/ministeriosRoles`) as DocumentReference<MinisteriosRolesConfig>;
    return docData(configDocRef);
  }



  /**
   * Agrega un nuevo ministerio a la configuración.
   * @param churchId El ID de la iglesia.
   * @param ministerio El objeto ministerio a agregar.
   * @returns Una Promesa que se resuelve cuando la operación está completa.
   */
  async addMinisterio(churchId: string, ministerio: MinisterioConfig): Promise<void> {
    const configDocRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/ministeriosRoles`);
    await updateDoc(configDocRef, {
      ministerios: arrayUnion(ministerio)
    });
  }

  /**
   * Actualiza un ministerio existente en la configuración usando una transacción.
   * Esta es una forma más segura de modificar elementos dentro de un array en Firestore
   * para evitar condiciones de carrera.
   * @param churchId El ID de la iglesia.
   * @param updatedMinisterio El objeto ministerio con los datos actualizados.
   * @returns Una Promesa que se resuelve cuando la operación está completa.
   */
  async updateMinisterio(churchId: string, updatedMinisterio: Partial<MinisterioConfig>): Promise<void> {
    // Obtener la referencia al documento de configuración, tipado para MinisteriosRolesConfig
    const configDocRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/ministeriosRoles`) as DocumentReference<MinisteriosRolesConfig>;

    // Ejecutar una transacción para asegurar la atomicidad
    await runTransaction(this.firestore, async (transaction) => {
      // Obtener el documento de configuración dentro de la transacción
      const configDoc = await transaction.get(configDocRef);
      const config = configDoc.data();

      if (!config || !config.ministerios) {
        throw new Error("Configuración no encontrada.");
      }

      // Obtener una copia del array ministerios
      const ministerios = [...config.ministerios]; // Usar spread para crear una copia mutable
      // Encontrar el índice del ministerio a actualizar por su ID
      const index = ministerios.findIndex(m => m.id === updatedMinisterio.id);

      if (index > -1) {
        // Fusionar el objeto existente con el objeto de actualización
        ministerios[index] = { ...ministerios[index], ...updatedMinisterio };
        // Establecer el array actualizado completo de nuevo en el documento dentro de la transacción
        transaction.update(configDocRef, { ministerios: ministerios });
      } else {
        // Opcional: Manejar el caso de que el ministerio no se encuentre.
        // Puedes lanzar un error si lo prefieres: throw new Error("Ministerio no encontrado");
      }
    });
  }


  /**
   * Elimina un ministerio de la configuración.
   * Nota: Utiliza arrayRemove, que puede tener limitaciones con objetos complejos
   * si no coinciden exactamente con el objeto almacenado en Firestore.
   * Un enfoque más robusto (leer, filtrar, escribir con set/update) podría ser necesario.
   * @param churchId El ID de la iglesia.
   * @param ministerioToDelete El objeto ministerio a eliminar.
   * @returns Una Promesa que se resuelve cuando la operación está completa.
   */
  async deleteMinisterio(churchId: string, ministerioToDelete: MinisterioConfig): Promise<void> {
    const configDocRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/ministeriosRoles`);
    // Usar arrayRemove para eliminar el objeto ministerio del array
    // arrayRemove requiere que el objeto a remover sea exactamente igual al objeto en el array de Firestore.
    await updateDoc(configDocRef, {
      ministerios: arrayRemove(ministerioToDelete)
    });
  }

  // Métodos placeholder para gestionar roles generales si son configurables
  async addGeneralRole(churchId: string, role: string): Promise<void> {
    const configDocRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/ministeriosRoles`);
    await updateDoc(configDocRef, {
      rolesGenerales: arrayUnion(role)
    });
  }

  async removeGeneralRole(churchId: string, role: string): Promise<void> {
    const configDocRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/ministeriosRoles`);
    await updateDoc(configDocRef, {
      rolesGenerales: arrayRemove(role)
    });
  }

  // Nota: Actualizar un rol general específico implicaría eliminar el antiguo y agregar el nuevo.
}
